import React from 'react';
import { useSelector } from 'react-redux';
import { OpTable, IOpTableProps } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { RootState } from 'store/store';
import { notification } from 'antd';
import { TABLE_HEIGHT } from 'constants/ui';
import { formatFullName, hasPermission } from 'utils/utils';
import { Role } from 'types/roleTypes';
import { deleteRequest, getRequest, postRequest } from 'api/apiClient';
import { User } from 'types/userTypes';
import { ROLE_DELETED_ID } from 'constants/userActivities';
import { useTranslation } from 'react-i18next';

interface RolesTableProps {
    roles: Role[];
    loading: boolean;
    setRoles: (role: Role[]) => void;
    handleEditClick: (role: Role) => void;
    handleAddClick: () => void;
}

const RolesTable: React.FC<RolesTableProps> = ({ roles, loading, setRoles, handleEditClick, handleAddClick }) => {
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasRoleWrite = hasPermission(tokenScopeList, orgId, 'o', 'role:w');
    const { t } = useTranslation();

    const handleDeleteRole = async (role: Role) => {
        if (role.admin) {
            notification.error({
                message: t('notification.error'),
                description: t('role.notification.deleteErrorCanNotDelete', { roleName: role.name }),
                placement: 'bottomRight',
            });
            return;
        }

        try {
            // Fetch users in the role
            const usersInRoleResponse = await getRequest(`/orgs/${orgId}/roles/${role.id}/users`);
            const usersWithoutOtherRoles = usersInRoleResponse.data.filter((user: User) => Number(user.roleCount) === 1);

            if (usersWithoutOtherRoles.length > 0) {
                const userNames = usersWithoutOtherRoles.map((user: User) =>
                    formatFullName(user.identity.firstName, user.identity.middleName, user.identity.lastName)
                ).join(', ');
                notification.error({
                    message: t('notification.error'),
                    description: t('role.notification.deleteErrorOnlyRole', { userNames: userNames }),
                    placement: 'bottomRight',
                });
                return;
            }

            await deleteRequest(`/orgs/${orgId}/roles/${role.id}`);
            await postRequest(`/orgs/${orgId}/userActivity`, {
                userId: globalUserId,
                activityId: ROLE_DELETED_ID,
                details: role.name
            });

            // Re-fetch the updated list of roles
            const updatedRoles = await getRequest(`/orgs/${orgId}/roles`);
            const filteredRoles = updatedRoles.data.filter((role: Role) => role.status === 1);
            setRoles(filteredRoles);

            notification.success({
                message: t('notification.success'),
                description: t('role.notification.deleteSuccess'),
                placement: 'bottomRight',
            });
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('role.notification.deleteError'),
                placement: 'bottomRight',
            });
        }
    };

    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'name',
            label: t('role.table.role'),
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        },
        {
            dataIndex: 'description',
            label: t('role.table.description'),
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
        },
        {
            dataIndex: 'userCount',
            label: t('role.table.userCount'),
            filter: {
                type: 'input',
            },
            sorter: (a, b) => a.userCount - b.userCount
        },
    ];

    const opTableProps: IOpTableProps = {
        dataSource: roles,
        columns: columns,
        rowActions: {
            onEditClick: (role: Role) => handleEditClick(role),
            onDeleteClick: hasRoleWrite ? handleDeleteRole : undefined,
            deleteModalTitle: () => t('confirmDelete.title', { title: t('role.itemName') }),
            deleteModalContent: ({ record }) => t('confirmDelete.message', { name: record.name }),
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: true,
        writeAccess: hasRoleWrite,
        allowAddition: hasRoleWrite ? {
            itemName: t('role.itemName'),
            onClick: () => handleAddClick(),
        } : false,
        loading: loading,
        allowExport: true,
        allowShowHideColumns: true,
    };

    return <OpTable {...opTableProps} />;
};

export default RolesTable;
